// src/pages/Sop.jsx
// Console → SOP (3.62.0, owner, 2026-10-13/15): the Super Admin SOP, the A-to-Z
// guide to how the Fax Portal works and how to run it. The chapters come from
// the backend (/portal/sop, Super Admins only; written in the backend's
// sop/chapters), so the text is never in the portal's code. Contents on the
// left, search, one chapter at a time, Download PDF, and each chapter's videos
// with whether they're made and still match the chapter.
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from '../components/Notifications';
import { API_BASE } from '../firebase/config';
import { usePhone } from '../hooks/useWindowWidth';
import Loader from '../components/Loader';
import Emo from '../components/Emo';

const VIDEO = {
  'not-made': { label: 'Video not made yet', color: 'var(--t-78909c)', bg: 'var(--c-f5f5f5)' },
  'needs-rerecording': { label: 'Video needs re-recording (the chapter changed)', color: 'var(--t-8a5a00)', bg: 'var(--c-fff8e1)' },
  ready: { label: 'Video ready', color: 'var(--t-2e7d32)', bg: 'var(--c-e8f5e9)' },
};

export default function Sop() {
  const { getToken } = useAuth();
  const { notify } = useNotify();
  const phone = usePhone();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(() => { try { return window.sessionStorage.getItem('faxPortal.sopChapter') || ''; } catch { return ''; } });
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(null);   // { title, url }
  async function watch(v) {
    try {
      const r = await fetch(`${API_BASE}/portal/sop/video/${encodeURIComponent(v.id)}`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Couldn't open the video.");
      setPlaying({ title: v.title, url: j.url });
    } catch (e) { notify({ tone: 'error', title: "Couldn't open the video", detail: e.message }); }
  }

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/portal/sop`, { headers: { Authorization: `Bearer ${await getToken()}` } });
        const j = await r.json();
        if (!r.ok) throw new Error(j.error || "Couldn't load the SOP.");
        setData(j);
      } catch (e) { setError(e.message); }
    })();
  }, [getToken]);

  const chapters = useMemo(() => data?.chapters || [], [data]);
  const term = q.trim().toLowerCase();
  const hits = useMemo(() => (term ? chapters.filter(c => `${c.title} ${c.text}`.toLowerCase().includes(term)) : chapters), [chapters, term]);
  const current = chapters.find(c => c.id === open) || chapters[0];
  const show = id => { setOpen(id); try { window.sessionStorage.setItem('faxPortal.sopChapter', id); } catch { /* not kept */ } window.scrollTo({ top: 0, behavior: 'smooth' }); };

  // Links between chapters inside the text ([text](#chapter-id))
  const onTextClick = e => { const a = e.target.closest?.('a[data-sop]'); if (a) { e.preventDefault(); show(a.getAttribute('data-sop')); } };

  async function downloadPdf() {
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/portal/sop/pdf`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Download failed');
      const url = URL.createObjectURL(await res.blob());
      const name = (res.headers.get('Content-Disposition') || '').match(/filename="?([^"]+)"?/)?.[1] || 'Fax_Portal_Super_Admin_SOP.pdf';
      const a = Object.assign(document.createElement('a'), { href: url, download: name });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { notify({ tone: 'error', title: "Couldn't download the SOP", detail: e.message }); }
    setBusy(false);
  }

  if (error) return <div style={S.error}>{error}</div>;
  if (!data) return <Loader label="Loading the SOP…" />;
  if (!chapters.length) return <p style={S.muted}>No chapters yet.</p>;

  const contents = (
    <nav aria-label="SOP chapters" style={phone ? S.navPhone : S.nav}>
      {hits.length === 0 && <div style={S.muted}>No chapter mentions “{q}”.</div>}
      {hits.map(c => (
        <button key={c.id} type="button" onClick={() => show(c.id)} aria-current={current?.id === c.id ? 'page' : undefined}
          style={{ ...S.navItem, ...(current?.id === c.id ? S.navOn : {}) }}>
          <span style={S.navN}>{c.n}</span><span>{c.title}</span>
          {c.videos.some(v => v.status === 'needs-rerecording') && <span title="A video needs re-recording" style={{ marginLeft: 'auto' }}><Emo e="🎬" /></span>}
        </button>
      ))}
    </nav>
  );

  return (
    <div>
      <div style={S.top}>
        <span />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search the SOP" aria-label="Search the SOP" style={S.input} />
          <button type="button" onClick={downloadPdf} disabled={busy} style={S.ghostBtn}>{busy ? 'Preparing…' : <><Emo e="📥" /> Download PDF</>}</button>
        </div>
      </div>
      <div style={phone ? undefined : S.layout}>
        {contents}
        {current && (
          <article style={S.article}>
            <div style={S.chapterN}>Chapter {current.n}{current.updatedIn ? ` · updated in ${current.updatedIn}` : ''}</div>
            <h2 style={S.h2}>{current.title}</h2>
            {current.videos.length > 0 && (
              <div style={S.videos}>
                {current.videos.map(v => {
                  const st = VIDEO[v.status] || VIDEO['not-made'];
                  return (
                    <div key={v.id} style={{ ...S.video, background: st.bg }}>
                      <Emo e="🎬" /> <strong>{v.title}</strong>
                      <span style={{ color: st.color, fontSize: 12 }}> · {st.label}</span>
                      {v.playable && <button type="button" onClick={() => watch(v)} style={{ ...S.ghostBtn, padding: '3px 10px', fontSize: 12, marginLeft: 8 }}><Emo e="▶" /> Watch</button>}
                    </div>
                  );
                })}
              </div>
            )}
            {playing && (
              <div style={S.player}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <strong style={{ fontSize: 13 }}>{playing.title}</strong>
                  <button type="button" onClick={() => setPlaying(null)} style={{ ...S.ghostBtn, padding: '3px 10px', fontSize: 12 }}>Close</button>
                </div>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video src={playing.url} controls autoPlay style={{ width: '100%', borderRadius: 8, background: '#000' }} />
              </div>
            )}
            <style>{SOP_CSS}</style>
            {/* the backend builds this HTML from the chapter files (escaped Markdown) */}
            <div className="sop-text" onClick={onTextClick} dangerouslySetInnerHTML={{ __html: current.html }} />
            <div style={S.pager}>
              {(() => { const i = chapters.findIndex(c => c.id === current.id); return (<>
                {i > 0 ? <button type="button" style={S.ghostBtn} onClick={() => show(chapters[i - 1].id)}>← {chapters[i - 1].n}. {chapters[i - 1].title}</button> : <span />}
                {i < chapters.length - 1 && <button type="button" style={S.ghostBtn} onClick={() => show(chapters[i + 1].id)}>{chapters[i + 1].n}. {chapters[i + 1].title} →</button>}
              </>); })()}
            </div>
          </article>
        )}
      </div>
    </div>
  );
}

const SOP_CSS = `
.sop-text{font-size:14.5px;line-height:1.65;color:var(--t-37474f)}
.sop-text h2{font-size:19px;color:var(--t-00695c);margin:26px 0 8px}
.sop-text h3{font-size:16.5px;color:var(--t-00695c);margin:22px 0 6px}
.sop-text h4{font-size:15px;color:var(--t-263238);margin:18px 0 4px}
.sop-text p{margin:0 0 12px}
.sop-text ul,.sop-text ol{margin:0 0 12px;padding-left:24px}
.sop-text li{margin:3px 0}
.sop-text li.sub{margin-left:18px;list-style:circle}
.sop-text code{background:var(--c-f5f5f5);border-radius:4px;padding:1px 5px;font-size:13px}
.sop-text a{color:var(--t-00897b)}
.sop-text hr{border:none;border-top:1px solid var(--c-e0f2f1);margin:20px 0}
.sop-text .sop-note{border-left:4px solid var(--c-00897b);background:var(--c-e0f2f1);padding:10px 14px;border-radius:6px;margin:0 0 14px}
.sop-text .sop-table{overflow-x:auto;margin:0 0 14px}
.sop-text table{border-collapse:collapse;width:100%;font-size:13.5px}
.sop-text th{background:var(--c-00695c);color:var(--t-ffffff);text-align:left;padding:8px 10px;font-weight:700}
.sop-text td{border-bottom:1px solid var(--c-e0f2f1);padding:8px 10px;vertical-align:top}
`;

const S = {
  top: { display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 },
  muted: { color: 'var(--t-78909c)', fontSize: 13, margin: 0 },
  input: { padding: '8px 12px', border: '1.5px solid var(--c-e0e0e0)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', minWidth: 220, background: 'var(--c-ffffff)', color: 'var(--t-37474f)' },
  ghostBtn: { padding: '8px 14px', background: 'var(--c-ffffff)', color: 'var(--t-546e7a)', border: '1.5px solid var(--c-e0e0e0)', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  layout: { display: 'grid', gridTemplateColumns: '260px 1fr', gap: 18, alignItems: 'start' },
  nav: { position: 'sticky', top: 90, display: 'flex', flexDirection: 'column', gap: 2, background: 'var(--c-ffffff)', border: '1px solid var(--c-e0f2f1)', borderRadius: 12, padding: 8 },
  navPhone: { display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, marginBottom: 8 },
  navItem: { display: 'flex', gap: 8, alignItems: 'center', textAlign: 'left', padding: '8px 10px', border: 'none', background: 'transparent', borderRadius: 8, cursor: 'pointer', fontSize: 13.5, color: 'var(--t-37474f)', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  navOn: { background: 'var(--c-e0f2f1)', color: 'var(--t-00695c)', fontWeight: 700 },
  navN: { minWidth: 20, color: 'var(--t-90a4ae)', fontWeight: 700 },
  article: { background: 'var(--c-ffffff)', border: '1px solid var(--c-e0f2f1)', borderRadius: 12, padding: '18px 24px', minWidth: 0 },
  chapterN: { fontSize: 12, color: 'var(--t-90a4ae)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' },
  h2: { margin: '4px 0 14px', fontSize: 24, color: 'var(--t-00695c)' },
  videos: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 },
  video: { padding: '8px 12px', borderRadius: 8, fontSize: 13, color: 'var(--t-37474f)' },
  player: { margin: '0 0 16px', padding: 10, border: '1px solid var(--c-e0f2f1)', borderRadius: 10, background: 'var(--c-fafafa)' },
  pager: { display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 24, flexWrap: 'wrap' },
  error: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '12px 18px', borderRadius: 8, fontSize: 14 },
};
